
import { useEffect, useState } from 'react';
import { createHero, getHero, updateHero } from '../../../../api/apiServices';

const initialHeroContent = {
  mainHeading: 'Affordable Style, Endless Beauty',
  subheading: 'Discover the latest trends in fashion at unbeatable prices',
  ctaButtonText: 'Shop Now',
  ctaButtonLink: '/',
  backgroundImage:
    'https://readdy.ai/api/search-image?query=elegant%20fashion%20background%20with%20soft%20pink%20tones%20minimalist%20abstract%20design%20luxury%20textile%20texture%20sophisticated%20feminine%20aesthetic%20clean%20modern%20style%20high%20end%20fashion%20photography&width=1920&height=800&seq=hero-bg&orientation=landscape',
};

export default function HeroEditor() {
  const [heroContent, setHeroContent] = useState(initialHeroContent);
  const [isSaved, setIsSaved] = useState(false);

  const [hero, setHero] = useState({
    title: "",
    subtitle: "",
    description: "",
    buttonText: "",
    heroImage: "", 
    isHeroActive: true,
  });

  const [backgroundFile, setBackgroundFile] = useState(null);
  const [exists, setExists] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

   useEffect(() => {
    const fetchHero = async () => {
      try {
        const res = await getHero();
        if (res?.data) {
          setHero(res.data);
          setExists(true);
        }
      } catch {
        setExists(false);
      }
    };
    fetchHero();
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
  
      const formData = new FormData();
  
      formData.append("title", hero.title);
      formData.append("subtitle", hero.subtitle);
      formData.append("description", hero.description);
      formData.append("buttonText", hero.buttonText);
      formData.append("isActive", hero.isHeroActive);
  
      if (backgroundFile) {
        formData.append("heroImage", backgroundFile);
      }
  
      if (exists) {
        await updateHero(formData);
      } else {
        await createHero(formData);
        setExists(true);
      }
  
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error("Hero save failed:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setHeroContent(initialHeroContent);
  };

  return (
    <div className="space-y-6">
      {/* Preview Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Live Preview</h2>
        </div>
        <div className="relative h-64 md:h-96 overflow-hidden">
          <img
            src={hero.image}
            alt="Hero background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white px-4 max-w-3xl">
              <h1
                className="text-2xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {hero.title}
              </h1>
              <p className="text-sm md:text-lg lg:text-xl mb-4 md:mb-8 opacity-90">
                {hero.subtitle}
              </p>
              <button className="bg-lilac-400 text-white px-6 md:px-8 py-2 md:py-3 rounded-lg hover:bg-lilac-300 transition-colors text-sm md:text-lg font-medium whitespace-nowrap cursor-pointer">
                {hero.buttonText}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Editor Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Hero Section Content</h2>

        <div className="space-y-5">
          {/* Main Heading */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Main Heading *</label>
            <input
              type="text"
              value={hero.title}
              onChange={(e) => setHero({ ...hero, title: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lilac-400 focus:border-transparent text-sm"
              placeholder="Enter main heading"
            />
          </div>

          {/* Subheading */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subheading *</label>
            <textarea
              value={hero.subtitle}
              onChange={(e) => setHero({ ...hero, subtitle: e.target.value })}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lilac-400 focus:border-transparent text-sm resize-none"
              placeholder="Enter subheading text"
            />
          </div>

          {/* CTA Button Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* CTA Button Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Button Text *</label>
              <input
                type="text"
                value={hero.buttonText}
                onChange={(e) =>
                  setHero({ ...hero, buttonText: e.target.value })
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lilac-400 focus:border-transparent text-sm"
                placeholder="e.g., Shop Now"
              />
            </div>
          </div>

          {/* Background Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                setBackgroundFile(e.target.files[0]);
                setHero({
                  ...hero,
                  heroImage: URL.createObjectURL(e.target.files[0]),
                });
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lilac-400 focus:border-transparent text-sm"
              placeholder="https://example.com/hero-bg.jpg"
            />
            <p className="text-xs text-gray-500 mt-1">Recommended size: 1920x800px</p>
          </div>

          <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Enable Banner
                </label>
                <p className="text-xs text-gray-500 mt-1">Show this banner on the website</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setHero({ ...hero, isHeroActive: !hero.isHeroActive })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                  hero.isHeroActive ? 'bg-pink-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    hero.isHeroActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
            <span className="text-green-700 text-sm font-medium">
              Changes saved successfully!
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={handleReset}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Reset to Default
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 px-6 py-2.5 bg-lilac-400 text-white rounded-lg hover:bg-lilac-300 transition-colors whitespace-nowrap cursor-pointer"
          >
            {isSaving ? (
            <>
              <i className="ri-loader-4-line animate-spin" /> Saving…
            </>
          ) : (
            <>
              <i className="ri-save-line" /> Save Changes
            </>
          )}
          </button>
        </div>
      </div>
    </div>
  );
}
